import {
  EventEmitter,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import {
  createClient,
  LiveTranscriptionEvents,
  SOCKET_STATES,
} from '@deepgram/sdk';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, of, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../../environments/environment';
import * as fromGenerated from '../../../../../_generated';
import * as fromAnalytics from '../../../../../analytics';
import * as fromFeatureFlags from '../../../../../feature-flags';
import { NetworkService } from '../../../../../shared/services/network.service';
import * as fromChats from '../../../../store';
import { AudioCaptureService } from '../audio-capture.service';
import { MicrophoneAccessError } from '../microphone-access-error';
import { MicrophoneAccessService } from '../microphone-access.service';
import {
  SpeechToTextServiceInterface,
  SpeechToTextServiceStateType,
  SpeechToTextStreamingResult,
  SpeechToTextTranscriptionResult,
} from '../prompt-input-speech-to-text.interfaces';
import * as fromHelper from '../speech-to-text.helper';
import { DeepgramApiKeyError } from './deepgram-api-key-error';
import { DeepgramAudioCaptureError } from './deepgram-audio-capture-error';
import { DeepgramConnectionLostError } from './deepgram-connection-lost-error';
import { DeepgramTranscriptionSetupError } from './deepgram-transcription-setup-error';
import {
  DeepgramLiveClient,
  DeepgramTranscriptResult,
  DEFAULT_DEEPGRAM_OPTIONS,
} from './deepgram.config';
import { NetworkUnavailableError } from './network-unavailable-error';

/** Interval for connectivity checks during active STT sessions (in milliseconds) */
const CONNECTIVITY_CHECK_INTERVAL_MS = 5000;
/** Timeout for the WebSocket connection to open (in milliseconds) */
const CONNECTION_TIMEOUT_MS = 10000;

/**
 * Union type for all STT error reasons used in analytics and Sentry tracking.
 */
type SttErrorReason =
  | 'network_unavailable'
  | 'network_unstable'
  | 'microphone_access_not_granted'
  | 'api_key_generation_error'
  | 'audio_capture_error'
  | 'transcription_setup_error'
  | 'unknown_error';

/**
 * WebSocket close codes (RFC 6455).
 */
const WEBSOCKET_CLOSE_CODES = {
  NORMAL_CLOSURE: 1000,
  GOING_AWAY: 1001,
  ABNORMAL_CLOSURE: 1006,
} as const;

@Injectable()
export class SpeechToTextDeepgramService
  implements SpeechToTextServiceInterface, OnDestroy
{
  private readonly deepgramService =
    inject<fromGenerated.SpeechToTextDeepgramService>(
      fromGenerated.SpeechToTextDeepgramService,
    );
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  private readonly ffSttDebugLog = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectFeatureFlag<boolean>(
      'ff-stt-debug-log',
    ),
  );
  private readonly audioCaptureService = inject(AudioCaptureService);
  private readonly microphoneAccessService = inject(MicrophoneAccessService);
  private readonly networkService = inject(NetworkService);
  private readonly analyticsService = inject(fromAnalytics.AnalyticsService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly chatsStore = inject<Store<fromChats.ChatsState>>(Store);

  /** This is the transcription chunks in process. It is filled with the transcription chunks as they are returned from the deepgram client. */
  private transcriptionChunksInProcess: string[] = [];

  public readonly state = signal<SpeechToTextServiceStateType>('inactive');
  public readonly transcriptionCompleted =
    new EventEmitter<SpeechToTextTranscriptionResult>();
  public readonly streamResult =
    new EventEmitter<SpeechToTextStreamingResult>();

  private audioCapture:
    | {
        mediaStream: MediaStream;
        audioWorkletNode: AudioWorkletNode;
        microphoneStream: MediaStreamAudioSourceNode;
        sampleRate: number;
      }
    | undefined;
  private listenLive: DeepgramLiveClient | undefined;
  private connectionStartTime = 0;
  /** Unique transaction ID (UUID) that tracks a single STT flow from start to inactive */
  private transactionId: string | undefined;
  /** Number of times connectivity dropped (transitioned from online to offline) during the current STT session */
  private currentSessionConnectivityDrops = 0;
  /** Subscription for periodic connectivity checks during STT session */
  private connectivityCheckSubscription: Subscription | undefined;

  // ------------------------------
  // Public API
  // ------------------------------

  /**
   * Starts the voice transcription by setting up the audio capture
   * and establishing the Deepgram WebSocket connection.
   */
  public async start(): Promise<void> {
    try {
      const isOnline = await this.networkService.checkConnectivity();

      // Check network availability before starting
      if (!isOnline) {
        throw new NetworkUnavailableError(
          'Cannot start speech-to-text when offline',
        );
      }

      // Generate a new transaction ID for this flow
      this.transactionId = uuidv4();

      const previousState = this.state();
      this.state.set('starting');
      this.analyticsService
        .sendDeepgramSttEvent({
          previousState,
          currentState: 'starting',
          reason: 'start_recording',
          transactionId: this.transactionId,
          connectionStartTime: this.connectionStartTime,
          transcriptionChunks: this.transcriptionChunksInProcess,
          currentSessionConnectivityDrops: this.currentSessionConnectivityDrops,
        })
        .subscribe();
      this.logDebugState();

      const mediaStream =
        await this.microphoneAccessService.requestMediaStream();
      const accessToken = await this.getTemporaryToken().catch((error) => {
        throw new DeepgramApiKeyError(
          'Failed to generate Deepgram API key',
          error,
        );
      });
      await this.startAudioCapture(mediaStream).catch((error) => {
        throw new DeepgramAudioCaptureError(
          'Failed to start audio capture',
          error,
        );
      });

      await this.startLiveTranscription(accessToken);
    } catch (error) {
      this.transitionToError(error);
      this.transitionToInactive();
    }
  }

  /**
   * Finalizes the transcription by requesting a clean connection closure.
   * The 'Close' event handler will transition to 'completed' state.
   */
  public finalize() {
    const previousState = this.state();
    this.state.set('finalizing');
    this.analyticsService
      .sendDeepgramSttEvent({
        previousState,
        currentState: 'finalizing',
        reason: 'finalize_requested',
        transactionId: this.transactionId ?? '',
        connectionStartTime: this.connectionStartTime,
        transcriptionChunks: this.transcriptionChunksInProcess,
        currentSessionConnectivityDrops: this.currentSessionConnectivityDrops,
      })
      .subscribe();
    this.logDebugState();

    this.listenLive?.requestClose();
  }

  /**
   * Aborts the transcription immediately without emitting results.
   *
   * Behavior:
   * - Sets state to 'aborting' and sends analytics event
   * - If `listenLive` exists: calls `finalize()` and `requestClose()`,
   *   then waits for Close event handler to transition to 'inactive'
   * - If `listenLive` is undefined: directly calls `transitionToInactive()`
   *   (handles case where connection never started or was already cleaned up)
   *
   * Note: This method does not emit transcription results. Any partial transcription
   * is discarded when transitioning to inactive.
   *
   * Guards: Returns early if already in 'aborting' or 'inactive' state.
   */
  public abort() {
    const currentState = this.state();

    // Guard: Don't abort if already aborting or inactive
    if (currentState === 'aborting' || currentState === 'inactive') {
      return;
    }

    const previousState = currentState;
    this.state.set('aborting');
    this.analyticsService
      .sendDeepgramSttEvent({
        previousState,
        currentState: 'aborting',
        reason: 'abort_requested',
        transactionId: this.transactionId ?? '',
        connectionStartTime: this.connectionStartTime,
        transcriptionChunks: this.transcriptionChunksInProcess,
        currentSessionConnectivityDrops: this.currentSessionConnectivityDrops,
      })
      .subscribe();
    this.logDebugState();

    // If listenLive exists, finalize and request close.
    // The Close event handler will transition to inactive when the connection closes.
    // If listenLive doesn't exist (connection never started), transition directly to inactive.
    if (this.listenLive) {
      this.listenLive.finalize();
      this.listenLive.requestClose();
    } else {
      this.transitionToInactive();
    }
  }

  // ------------------------------
  // State Transitions
  // ------------------------------

  /**
   * Transitions to the transcripting state.
   * Called from the WebSocket Open event handler, which sets connectionStartTime before calling this method.
   */
  private transitionToTranscripting() {
    const previousState = this.state();
    this.state.set('transcripting');
    this.analyticsService
      .sendDeepgramSttEvent({
        previousState,
        currentState: 'transcripting',
        reason: 'connection_opened',
        transactionId: this.transactionId ?? '',
        connectionStartTime: this.connectionStartTime,
        transcriptionChunks: this.transcriptionChunksInProcess,
        currentSessionConnectivityDrops: this.currentSessionConnectivityDrops,
      })
      .subscribe();
    this.startConnectivityMonitoring();
    this.logDebugState();
  }

  /**
   * Transitions to the completed state, emits the transcription result,
   * and then transitions to inactive.
   */
  private transitionToCompleted() {
    const previousState = this.state();
    this.state.set('completed');
    this.analyticsService
      .sendDeepgramSttEvent({
        previousState,
        currentState: 'completed',
        reason: 'connection_closed',
        transactionId: this.transactionId ?? '',
        connectionStartTime: this.connectionStartTime,
        transcriptionChunks: this.transcriptionChunksInProcess,
        currentSessionConnectivityDrops: this.currentSessionConnectivityDrops,
      })
      .subscribe();
    this.transcriptionCompleted.emit({
      raw: this.transcriptionChunksInProcess,
      clean: fromHelper.transcriptionChunksToCleanString(
        this.transcriptionChunksInProcess,
      ),
    });
    this.transitionToInactive();
  }

  /**
   * Transitions to the inactive state and cleans up all resources.
   * Resets connectionStartTime to 0 and transactionId for the next session.
   *
   * Guards: Returns early if already in 'inactive' state to avoid duplicate analytics events.
   */
  private transitionToInactive() {
    const currentState = this.state();

    // Guard: Don't transition if already inactive
    if (currentState === 'inactive') {
      return;
    }

    const previousState = currentState;
    this.state.set('inactive');
    this.analyticsService
      .sendDeepgramSttEvent({
        previousState,
        currentState: 'inactive',
        reason: 'connection_closed',
        transactionId: this.transactionId ?? '', // Use existing transactionId if available
        connectionStartTime: this.connectionStartTime,
        transcriptionChunks: this.transcriptionChunksInProcess,
        currentSessionConnectivityDrops: this.currentSessionConnectivityDrops,
      })
      .subscribe();
    this.stopConnectivityMonitoring();
    this.transcriptionChunksInProcess = [];
    this.audioCaptureService.cleanup();
    this.audioCapture = undefined;
    // Reset connection start time and transaction ID for next session
    this.connectionStartTime = 0;
    this.transactionId = undefined;
    this.currentSessionConnectivityDrops = 0;
    this.logDebugState();
  }

  // ------------------------------
  // Connectivity monitoring
  // ------------------------------

  /**
   * Starts periodic connectivity monitoring during an active STT session.
   * Subscribes to `NetworkService.periodicConnectivityChecks()` which runs
   * `checkConnectivity()` every CONNECTIVITY_CHECK_INTERVAL_MS and updates the network signal.
   *
   * This method only keeps the subscription and transition handling (toast, analytics);
   * the interval logic lives in NetworkService.
   *
   * **Transition Detection**: Only tracks **transitions** from online to offline:
   * - On transition (online → offline): increments `currentSessionConnectivityDrops`, shows toast, sends analytics
   * - Consecutive offline results do NOT increment the counter or trigger events
   *
   * @private
   */
  private startConnectivityMonitoring(): void {
    this.stopConnectivityMonitoring();

    let wasOnline = true; // Assume online at start since we just connected

    this.connectivityCheckSubscription = this.networkService
      .periodicConnectivityChecks(CONNECTIVITY_CHECK_INTERVAL_MS)
      .pipe(
        catchError(() => of(false)), // Treat errors as offline so we don't break the stream
        tap((isOnline) => {
          const isTransitionToOffline = wasOnline && !isOnline;

          if (isTransitionToOffline) {
            this.currentSessionConnectivityDrops++;

            const toastMessage = this.translateService.instant(
              'PROMPT_INPUT.TOAST.STT_NETWORK_UNSTABLE',
            ) as string;
            this.toastrService.warning(toastMessage, undefined, {
              closeButton: true,
            });

            const currentState = this.state();
            const connectionDurationMs =
              this.connectionStartTime > 0
                ? Date.now() - this.connectionStartTime
                : undefined;
            this.analyticsService
              .sendDeepgramConnectionLostEvent({
                previousState: currentState,
                currentState,
                transactionId: this.transactionId ?? '',
                reason: 'network_unstable',
                userOnline: false,
                pageVisible: this.networkService.isPageVisible(),
                ...(connectionDurationMs !== undefined
                  ? { connectionDurationMs }
                  : {}),
                currentSessionConnectivityDrops:
                  this.currentSessionConnectivityDrops,
                toastMessage,
                consoleMessage:
                  '[Deepgram] Unstable network detected during transcription.',
                expected: true,
              })
              .subscribe();
          }

          wasOnline = isOnline;
        }),
      )
      .subscribe();
  }

  /**
   * Stops periodic connectivity monitoring.
   * Called when transitioning to inactive state or when the service is destroyed.
   *
   * @private
   */
  private stopConnectivityMonitoring(): void {
    if (this.connectivityCheckSubscription) {
      this.connectivityCheckSubscription.unsubscribe();
      this.connectivityCheckSubscription = undefined;
    }
  }

  // ------------------------------
  // Core Implementation
  // ------------------------------

  /**
   * Obtains a temporary Deepgram API key from the backend.
   *
   * For Deepgram, this is a temporary API key with limited scopes and 30-second expiration.
   * The key is created server-side to avoid exposing the main API key to clients.
   *
   * @throws DeepgramApiKeyError if API key generation fails
   */
  private async getTemporaryToken(): Promise<string> {
    if (this.ffSttDebugLog()) {
      // eslint-disable-next-line no-console
      console.log('Requesting Deepgram API key from backend...');
    }
    const newApiKey = await firstValueFrom(
      this.deepgramService.deepgramControllerGenerateApiKeyV1(),
    );

    if (this.ffSttDebugLog()) {
      // eslint-disable-next-line no-console
      console.log('Deepgram API key response:', {
        hasKey: !!newApiKey.key,
        keyLength: newApiKey.key?.length,
        keyId: newApiKey.id,
        createdAt: newApiKey.createdAt,
      });
    }

    return newApiKey.key;
  }

  /**
   * Starts the audio capture from microphone to Deepgram.
   *
   * Flow:
   * 1. Uses AudioCaptureService.getAudioCapture() with the provided MediaStream for AudioWorklet setup
   *    (AudioContext is automatically resumed by AudioCaptureService)
   * 2. Connects audio data from AudioWorklet to Deepgram WebSocket via onAudioData callback
   *
   * The AudioWorklet processes audio in real-time and sends PCM data
   * to the Deepgram WebSocket for live transcription.
   *
   * @param mediaStream - The MediaStream obtained from MicrophoneAccessService
   */
  private async startAudioCapture(mediaStream: MediaStream): Promise<void> {
    this.audioCapture = await this.audioCaptureService.getAudioCapture({
      mediaStream,
      onAudioData: (audioBuffer: ArrayBuffer) => {
        // Only send audio data when in transcripting state and Deepgram is ready
        if (
          this.state() === 'transcripting' &&
          this.listenLive?.getReadyState() === SOCKET_STATES.open
        ) {
          this.listenLive.send(audioBuffer);
        }
      },
    });
  }

  /**
   * Starts the Deepgram live transcription client and all event handlers.
   *
   * Steps:
   * 1. Creates the Deepgram SDK client with custom endpoints
   * 2. Configures live transcription options (language detection, sample rate)
   * 3. Establishes the WebSocket connection
   * 4. Registers event handlers for: Open, Transcript, Close, Error
   *
   * @param accessToken - The Deepgram API access token
   * @returns Promise that resolves when the WebSocket connection opens successfully
   * @throws DeepgramTranscriptionSetupError if setup fails or connection doesn't open within timeout
   */
  private startLiveTranscription(accessToken: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const deepgramSDK = createClient({
          accessToken,
          // Deepgram - Configuring Custom Endpoints:
          // https://developers.deepgram.com/reference/custom-endpoints
          global: {
            fetch: {
              options: {
                url: `https://${environment.deepgramBaseUrl}`,
              },
            },
            websocket: {
              options: {
                url: `wss://${environment.deepgramBaseUrl}`,
              },
            },
          },
        });

        // Set the actual sample rate from audio context (if available)
        const audioCaptureSampleRate = this.audioCaptureService.getSampleRate();

        this.listenLive = deepgramSDK.listen.live({
          ...DEFAULT_DEEPGRAM_OPTIONS,
          ...(audioCaptureSampleRate
            ? // eslint-disable-next-line camelcase
              { sample_rate: audioCaptureSampleRate }
            : {}),
        });

        // Set up timeout to reject if connection doesn't open within reasonable time
        const connectionTimeout = setTimeout(() => {
          reject(
            new DeepgramTranscriptionSetupError(
              'WebSocket connection timeout: Connection did not open within expected time',
            ),
          );
        }, CONNECTION_TIMEOUT_MS);

        this.listenLive?.on(LiveTranscriptionEvents.Open, () => {
          /* Guard: Only process Open event when in 'starting' state.
           * Prevents race conditions where Open fires after errors, during abort, or after closure.
           * Safety: If Open is skipped while in 'starting', the Close handler
           * will transition us out of 'starting' state, preventing stuck states. */
          if (this.state() !== 'starting') {
            return;
          }
          clearTimeout(connectionTimeout);
          this.connectionStartTime = Date.now();
          this.transitionToTranscripting();
          resolve();
        });

        this.listenLive?.on(
          LiveTranscriptionEvents.Transcript,
          (data: DeepgramTranscriptResult) => {
            /* Guard: Only process transcripts when in 'transcripting' or 'finalizing' state.
             * We allow 'finalizing' because final chunks can arrive after finalize() is called
             * but before the WebSocket closes. Prevents processing after errors or during inactive state. */
            if (
              this.state() !== 'transcripting' &&
              this.state() !== 'finalizing'
            ) {
              return;
            }

            const transcript =
              data?.channel?.alternatives?.[0]?.transcript?.trim();
            const isFinalTranscript =
              (data.is_final || data.speech_final) && !!transcript;

            if (this.ffSttDebugLog()) {
              // eslint-disable-next-line no-console
              console.log('STT Debug Log - Transcript result:', {
                transcript,
                isFinalTranscript,
                // eslint-disable-next-line camelcase
                is_final: data.is_final,
                // eslint-disable-next-line camelcase
                speech_final: data.speech_final,
              });
            }
            if (!isFinalTranscript) {
              return;
            }

            this.transcriptionChunksInProcess.push(transcript);

            this.streamResult.emit({
              transcript: fromHelper.transcriptionChunksToCleanString(
                this.transcriptionChunksInProcess,
              ),
              isFinal: isFinalTranscript,
              original: data,
            });
          },
        );

        this.listenLive?.on(
          LiveTranscriptionEvents.Close,
          (event: CloseEvent) => {
            /**
             * Indicates whether a WebSocket connection closed cleanly.
             *
             * The `event.wasClean` is `true` only when the closing handshake completes
             * successfully. It becomes `false` if the connection ends abruptly due to
             * network issues, server crashes, protocol errors, timeouts, or intermediaries
             * (like proxies or load balancers) dropping the TCP connection without sending
             * a proper close frame.
             */
            clearTimeout(connectionTimeout);
            if (event.wasClean) {
              // Clean close - finalize if we were finalizing, otherwise go to inactive
              if (this.state() === 'finalizing') {
                this.transitionToCompleted();
              } else {
                this.transitionToInactive();
              }
            } else {
              // Unexpected close - handle connection loss
              // Capture state before handling (handleConnectionLost will transition to error)
              const stateBeforeLoss = this.state();
              // Only calculate duration if connection was actually established (connectionStartTime > 0)
              const connectionDurationMs =
                this.connectionStartTime > 0
                  ? Date.now() - this.connectionStartTime
                  : 0;
              this.handleConnectionLost({
                code: event.code,
                reason: event.reason || 'Unknown reason',
                connectionDurationMs,
              });
              // If we were actively transcribing, finalize to save whatever we have
              // Otherwise, just transition to inactive
              if (
                stateBeforeLoss === 'transcripting' ||
                stateBeforeLoss === 'finalizing' ||
                stateBeforeLoss === 'starting'
              ) {
                this.transitionToCompleted();
              } else {
                this.transitionToInactive();
              }
            }
          },
        );

        /**
         * WebSocket error event handler
         * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/error_event
         *
         * Fired when the WebSocket connection has been closed due to an error
         * (e.g., some data couldn't be sent). This is a generic error event
         * that indicates a problem occurred, but doesn't provide detailed error information.
         *
         * Note: The error event itself contains minimal information. Specific details
         * about what went wrong are typically provided in the subsequent 'close' event,
         * which includes close codes and reasons. Our connection monitoring focuses on
         * the 'close' event for detailed error classification.
         */
        this.listenLive?.on(LiveTranscriptionEvents.Error, (error) => {
          clearTimeout(connectionTimeout);
          // Reject the promise if error occurs before connection opens
          if (this.state() === 'starting') {
            reject(
              new DeepgramTranscriptionSetupError(
                'WebSocket error occurred during connection setup',
                error,
              ),
            );
          } else {
            // Log error but don't reject if connection was already established
            console.error(error);
          }
        });
      } catch (error) {
        reject(
          new DeepgramTranscriptionSetupError(
            'Failed to start live transcription',
            error,
          ),
        );
      }
    });
  }

  // ------------------------------
  // Error Handling
  // ------------------------------

  /**
   * Transitions to the error state and handles error-specific logging, toast notifications,
   * Sentry reporting, and analytics tracking.
   *
   * This method centralizes all error handling logic for Deepgram STT errors.
   * Unknown error types are automatically wrapped in DeepgramTranscriptionSetupError.
   *
   * @param error - The error that occurred. Known error types are handled specifically,
   * unknown errors are wrapped in DeepgramTranscriptionSetupError.
   */
  private transitionToError(
    error:
      | DeepgramApiKeyError
      | DeepgramAudioCaptureError
      | DeepgramTranscriptionSetupError
      | DeepgramConnectionLostError
      | MicrophoneAccessError
      | NetworkUnavailableError
      // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
      | unknown,
  ): void {
    const previousState = this.state(); // Capture state before changing it
    this.state.set('error');
    const currentState = this.state() as 'error'; // Current state is now 'error'
    console.error(error);
    this.logDebugState();

    const selectedChatId = this.chatsStore.selectSignal(
      fromChats.chatsFeature.selectSelectedChatId,
    )();

    let toastMessage: string | undefined;
    let reason: SttErrorReason; // Reason for analytics and Sentry tag
    let errorForSentry: unknown = error; // Track which error to send to Sentry

    switch (true) {
      /**
       * Handle network unavailability errors.
       * User attempted to start STT while offline.
       * Show toast notification, send analytics event, and track to Sentry.
       */
      case error instanceof NetworkUnavailableError:
        reason = 'network_unavailable' as const;
        toastMessage = this.translateService.instant(
          'PROMPT_INPUT.TOAST.STT_NETWORK_UNAVAILABLE',
        ) as string;
        this.analyticsService
          .sendDeepgramSttEvent({
            previousState,
            currentState,
            reason: 'network_unavailable',
            transactionId: this.transactionId ?? '',
            connectionStartTime: this.connectionStartTime,
            transcriptionChunks: this.transcriptionChunksInProcess,
            currentSessionConnectivityDrops:
              this.currentSessionConnectivityDrops,
            toastMessage,
          })
          .subscribe();
        this.toastrService.warning(toastMessage, undefined, {
          closeButton: true,
        });
        // No connection metrics - network check happens before connection starts
        break;
      /**
       * Handle microphone access errors.
       * Show toast notification if microphone access was not granted.
       * Send analytics event with microphone access status.
       */
      case error instanceof MicrophoneAccessError:
        reason = 'microphone_access_not_granted' as const;
        toastMessage = this.translateService.instant(
          'PROMPT_INPUT.TOAST.STT_MICROPHONE_ACCESS_FAILED',
        ) as string;
        this.analyticsService
          .sendDeepgramMicrophoneAccessEvent({
            previousState,
            currentState,
            reason,
            transactionId: this.transactionId ?? '',
            toastMessage,
          })
          .subscribe();
        this.toastrService.warning(toastMessage, undefined, {
          closeButton: true,
        });
        // No connection metrics - microphone access happens before connection starts
        break;
      /**
       * Handle Deepgram API key errors.
       * Show toast notification if API key generation failed.
       * Send analytics event with API key generation error status.
       */
      case error instanceof DeepgramApiKeyError:
        reason = 'api_key_generation_error' as const;
        toastMessage = this.translateService.instant(
          'PROMPT_INPUT.TOAST.STT_ERROR_GENERATE_API_KEY_ERROR',
        ) as string;
        this.analyticsService
          .sendDeepgramApiKeyGenerationErrorEvent({
            previousState,
            currentState: currentState,
            transactionId: this.transactionId ?? '',
            toastMessage,
          })
          .subscribe();
        this.toastrService.warning(toastMessage, undefined, {
          closeButton: true,
        });
        break;
      /**
       * Handle Deepgram audio capture errors.
       * Show toast notification if audio capture failed.
       * Send analytics event with audio capture error status.
       */
      case error instanceof DeepgramAudioCaptureError:
        reason = 'audio_capture_error' as const;
        toastMessage = this.translateService.instant(
          'PROMPT_INPUT.TOAST.STT_ERROR_AUDIO_CAPTURE_FAILED',
        ) as string;
        this.analyticsService
          .sendDeepgramSttEvent({
            previousState,
            currentState,
            reason,
            transactionId: this.transactionId ?? '',
            connectionStartTime: this.connectionStartTime,
            transcriptionChunks: this.transcriptionChunksInProcess,
            currentSessionConnectivityDrops:
              this.currentSessionConnectivityDrops,
            toastMessage,
          })
          .subscribe();
        this.toastrService.warning(toastMessage, undefined, {
          closeButton: true,
        });
        break;
      /**
       * Handle Deepgram connection loss errors.
       * Connection loss uses a different analytics event type (sendDeepgramConnectionLostEvent).
       * Handles both expected (offline/tab hidden) and unexpected (online/tab visible) losses.
       */
      case error instanceof DeepgramConnectionLostError: {
        const connectionLossError = error;
        const isUserOnline = this.networkService.isOnline();
        const isPageVisible = this.networkService.isPageVisible();
        const consoleMessage = `[Deepgram] ${this.getConnectionLossMessage(
          connectionLossError.isExpected,
          isUserOnline,
        )}`;
        toastMessage = this.getConnectionLossToastMessage(
          connectionLossError.isExpected,
          isUserOnline,
        );

        this.logConnectionLoss(
          connectionLossError,
          consoleMessage,
          connectionLossError.isExpected,
        );
        this.toastrService.warning(toastMessage, undefined, {
          closeButton: true,
        });

        this.analyticsService
          .sendDeepgramConnectionLostEvent({
            previousState,
            currentState,
            transactionId: this.transactionId ?? '',
            reason: connectionLossError.reasonCategory,
            userOnline: isUserOnline,
            pageVisible: isPageVisible,
            ...(connectionLossError.connectionDurationMs === undefined
              ? {}
              : {
                  connectionDurationMs:
                    connectionLossError.connectionDurationMs,
                }),
            websocketCode: connectionLossError.code,
            websocketReason: connectionLossError.reason,
            consoleMessage,
            toastMessage,
            expected: connectionLossError.isExpected,
          })
          .subscribe();

        // Use specialized Sentry capture for connection loss (different tags/levels)
        this.captureConnectionLossToSentry(
          connectionLossError,
          consoleMessage,
          toastMessage,
          isUserOnline,
          isPageVisible,
          selectedChatId ?? undefined,
        );
        // Connection loss doesn't use regular reason for STT events, so skip regular Sentry capture
        // State transition (to completed or inactive) is handled by the caller
        return;
      }
      /**
       * Handle Deepgram transcription setup errors.
       * Show toast notification if transcription setup failed.
       * Send analytics event with transcription setup error status.
       */
      case error instanceof DeepgramTranscriptionSetupError:
        reason = 'transcription_setup_error' as const;
        toastMessage = this.translateService.instant(
          'PROMPT_INPUT.TOAST.STT_ERROR_TRANSCRIPTION_SETUP_FAILED',
        ) as string;
        this.analyticsService
          .sendDeepgramSttEvent({
            previousState,
            currentState,
            reason,
            transactionId: this.transactionId ?? '',
            connectionStartTime: this.connectionStartTime,
            transcriptionChunks: this.transcriptionChunksInProcess,
            currentSessionConnectivityDrops:
              this.currentSessionConnectivityDrops,
            toastMessage,
          })
          .subscribe();
        this.toastrService.warning(toastMessage, undefined, {
          closeButton: true,
        });
        break;
      default: {
        // Wrap unknown errors as transcription setup errors
        errorForSentry = new DeepgramTranscriptionSetupError(
          'Failed to start live transcription',
          error,
        );
        reason = 'unknown_error' as const;
        toastMessage = this.translateService.instant(
          'PROMPT_INPUT.TOAST.STT_ERROR_TRANSCRIPTION_SETUP_FAILED',
        ) as string;
        this.analyticsService
          .sendDeepgramSttEvent({
            previousState,
            currentState,
            reason,
            transactionId: this.transactionId ?? '',
            connectionStartTime: this.connectionStartTime,
            transcriptionChunks: this.transcriptionChunksInProcess,
            currentSessionConnectivityDrops:
              this.currentSessionConnectivityDrops,
            toastMessage,
          })
          .subscribe();
        this.toastrService.warning(toastMessage, undefined, {
          closeButton: true,
        });
        break;
      }
    }

    // Capture to Sentry with centralized logic
    this.captureErrorToSentry(
      reason,
      errorForSentry,
      toastMessage ?? '',
      previousState,
      currentState,
      selectedChatId ?? undefined,
    );
  }

  /**
   * Captures error to Sentry with consistent tagging and context.
   * Uses `reason` tag to match analytics events for easy correlation.
   * Simplified: Only includes essential data (calculated fields like duration/word count are in analytics).
   *
   * @param reason - Reason for the error (matches analytics event reason, e.g., 'network_unavailable')
   * @param error - The error object to capture
   * @param toastMessage - Toast message shown to the user
   * @param previousState - STT service state before transitioning to error
   * @param currentState - Current STT service state (should be 'error')
   * @param selectedChatId - Optional conversation ID for context
   */
  private captureErrorToSentry(
    reason: SttErrorReason,
    error: unknown,
    toastMessage: string,
    previousState: SpeechToTextServiceStateType,
    currentState: SpeechToTextServiceStateType,
    selectedChatId?: number,
  ): void {
    const errorMessage =
      error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : String(error);

    const exceptionToCapture =
      error instanceof Error ? error : new Error(errorMessage);

    const extras: Record<string, unknown> = {
      errorMessage,
      toastMessage,
      previousState,
      currentState,
    };

    if (selectedChatId) {
      extras['conversationId'] = selectedChatId;
    }

    // Include wrapper error details if available (for context when capturing originalError)
    if (
      error &&
      typeof error === 'object' &&
      'originalError' in error &&
      error instanceof Error
    ) {
      extras['wrapperError'] = {
        name: error.name,
        message: error.message,
      };
    }

    Sentry.withScope((scope) => {
      scope.setTag('deepgram_event', 'stt_error');
      scope.setTag('reason', reason); // Use reason tag to match analytics events
      scope.setTag('stt_state', currentState);
      scope.setLevel('error');
      scope.setExtras(extras);
      Sentry.captureException(exceptionToCapture);
    });
  }

  // ------------------------------
  // Connection Loss Handling
  // ------------------------------

  /**
   * Gets the connection loss message based on expected status and network state.
   * Returns message without "[Deepgram] " prefix (add prefix when logging to console).
   *
   * **Message Selection Logic:**
   * - Unexpected loss: Always returns error message (user online and tab visible)
   * - Expected loss + offline: Returns offline message
   * - Expected loss + online: Returns tab hidden message (user was online but tab was hidden)
   *
   * **Usage:**
   * - Used in error constructor (no prefix needed)
   * - Used in console logging (prefix added: `[Deepgram] ${message}`)
   *
   * @param isExpected - Whether the loss is expected (offline or tab hidden)
   * @param isUserOnline - Whether user is online (only relevant if isExpected is true)
   * @returns Connection loss message without prefix
   */
  private getConnectionLossMessage(
    isExpected: boolean,
    isUserOnline: boolean,
  ): string {
    if (isExpected) {
      return !isUserOnline
        ? 'Connection dropped (expected): User was offline.'
        : 'Connection dropped (expected): User was online and the tab was hidden.';
    }
    return 'Connection lost (unexpected): User was online and the tab was visible.';
  }

  /**
   * Gets the appropriate toast message for connection loss.
   */
  private getConnectionLossToastMessage(
    isExpected: boolean,
    isUserOnline: boolean,
  ): string {
    if (!isExpected) {
      return this.translateService.instant(
        'PROMPT_INPUT.TOAST.STT_CONNECTION_LOST_ERROR',
      ) as string;
    }

    const key = !isUserOnline
      ? 'PROMPT_INPUT.TOAST.STT_CONNECTION_LOST_OFFLINE'
      : 'PROMPT_INPUT.TOAST.STT_CONNECTION_LOST_TAB_HIDDEN';

    return this.translateService.instant(key) as string;
  }

  /**
   * Logs connection loss to console with appropriate level based on expected status.
   *
   * **Logging Levels:**
   * - Expected loss: `console.warn()` (warning level - expected behavior)
   * - Unexpected loss: `console.error()` (error level - potential issue)
   *
   * **Payload Includes:**
   * - WebSocket close code and reason
   * - Categorized reason (from `categorizeConnectionReason()`)
   * - Connection duration (if available)
   * - Expected flag
   *
   * @param error - DeepgramConnectionLostError containing connection loss details
   * @param message - Console message (should include "[Deepgram] " prefix)
   * @param isExpected - Whether the loss is expected (determines log level)
   */
  private logConnectionLoss(
    error: DeepgramConnectionLostError,
    message: string,
    isExpected: boolean,
  ): void {
    const payload = {
      code: error.code,
      reason: error.reason,
      reasonCategory: error.reasonCategory,
      connectionDurationMs: error.connectionDurationMs,
      expected: isExpected,
    };
    if (isExpected) {
      console.warn(message, payload);
    } else {
      console.error(message, payload);
    }
  }

  /**
   * Handles WebSocket connection loss with network- and visibility-aware logic.
   * Creates a DeepgramConnectionLostError and routes it through transitionToError().
   *
   * **Expected vs Unexpected Loss Logic:**
   * - **Expected**: User is offline OR tab is hidden → Loss is expected (browser/system behavior)
   * - **Unexpected**: User is online AND tab is visible → Loss is unexpected (potential issue)
   *
   * **Edge Cases:**
   * - If `connectionDurationMs` is undefined, it means the connection never fully established
   * - Network status is checked at the time of disconnect, not when connection started
   * - Page visibility is checked at disconnect time (user may have switched tabs during recording)
   *
   * @param event - Connection loss event with code, reason, and optional duration
   * @param event.code - WebSocket close code (1000=normal, 1001=going away, 1006=abnormal closure, etc.)
   * @param event.reason - WebSocket close reason string (may be empty)
   * @param event.connectionDurationMs - Optional duration of connection before loss (undefined if never established)
   */
  private handleConnectionLost(event: {
    code: number;
    reason: string;
    connectionDurationMs?: number;
  }): void {
    const isUserOnline = this.networkService.isOnline();
    const isPageVisible = this.networkService.isPageVisible();
    const isExpectedLoss = !isUserOnline || !isPageVisible;
    const reasonCategory = this.categorizeConnectionReason(
      event.code,
      event.reason,
      isUserOnline,
    );

    const error = new DeepgramConnectionLostError(
      this.getConnectionLossMessage(isExpectedLoss, isUserOnline),
      event.code,
      event.reason,
      isExpectedLoss,
      reasonCategory,
      event.connectionDurationMs,
    );

    this.transitionToError(error);
  }

  /**
   * Categorizes WebSocket close codes into meaningful reason strings for analytics and Sentry.
   *
   * **WebSocket Close Code Mapping:**
   * - `1000` (Normal Closure): Server/client closed connection cleanly → `'normal_closure'`
   * - `1001` (Going Away): Server/client is going away (e.g., server restart) → `'going_away'`
   * - `1006` (Abnormal Closure): Connection closed abnormally without close frame
   *   - If user is online → `'abnormal_closure'` (potential issue)
   *   - If user is offline → `'network_timeout'` (expected network issue)
   *
   * **Fallback Logic:**
   * - Unknown codes: Checks if reason string contains "timeout" → `'network_timeout'`
   * - Otherwise: Defaults to `'socket_closed_by_remote'`
   *
   * **Edge Cases:**
   * - Code `1006` categorization depends on network status at disconnect time
   * - Reason string may be empty or undefined (handled by `?? ''`)
   * - Case-insensitive matching for "timeout" in reason string
   *
   * @param code - WebSocket close code (RFC 6455 standard codes)
   * @param reason - WebSocket close reason string (may be empty or undefined)
   * @param isUserOnline - Whether user is online at disconnect time (affects code 1006 categorization)
   * @returns Categorized reason string for analytics and Sentry tracking
   */
  private categorizeConnectionReason(
    code: number,
    reason: string,
    isUserOnline: boolean,
  ): string {
    switch (code) {
      case WEBSOCKET_CLOSE_CODES.ABNORMAL_CLOSURE:
        return isUserOnline ? 'abnormal_closure' : 'network_timeout';
      case WEBSOCKET_CLOSE_CODES.GOING_AWAY:
        return 'going_away';
      case WEBSOCKET_CLOSE_CODES.NORMAL_CLOSURE:
        return 'normal_closure';
      default: {
        const reasonLower = (reason ?? '').toLowerCase();
        return reasonLower.includes('timeout')
          ? 'network_timeout'
          : 'socket_closed_by_remote';
      }
    }
  }

  /**
   * Captures connection loss to Sentry with connection loss specific tags.
   */
  private captureConnectionLossToSentry(
    error: DeepgramConnectionLostError,
    consoleMessage: string,
    toastMessage: string,
    isUserOnline: boolean,
    isPageVisible: boolean,
    conversationId?: number,
  ): void {
    Sentry.withScope((scope) => {
      scope.setTag('network_status', isUserOnline ? 'online' : 'offline');
      scope.setTag('page_visible', String(isPageVisible));
      scope.setTag('deepgram_event', 'connection_lost');
      scope.setTag(
        'connection_loss_type',
        error.isExpected ? 'expected' : 'unexpected',
      );
      scope.setLevel(error.isExpected ? 'warning' : 'error');
      scope.setExtras({
        websocketCode: error.code,
        websocketReason: error.reason,
        reasonCategory: error.reasonCategory,
        ...(error.connectionDurationMs === undefined
          ? {}
          : { connectionDurationMs: error.connectionDurationMs }),
        consoleMessage,
        toastMessage,
        expected: error.isExpected,
        ...(conversationId ? { conversationId } : {}),
      });
      if (error.isExpected) {
        Sentry.captureMessage(consoleMessage);
      } else {
        Sentry.captureException(error);
      }
    });
  }

  // ------------------------------
  // Utilities
  // ------------------------------

  private logDebugState() {
    if (this.ffSttDebugLog()) {
      // eslint-disable-next-line no-console
      console.log('STT Debug Log:', {
        state: this.state(),
        transcriptionChunks: this.transcriptionChunksInProcess.length,
        deepgramConnection: this.listenLive?.connectionState() ?? 'none',
        deepgramReadyState: this.listenLive?.getReadyState() ?? 'none',
        hasMicrophone: !!this.audioCapture?.mediaStream,
        hasAudioWorklet: !!this.audioCapture?.audioWorkletNode,
      });
    }
  }

  /**
   * Cleans up all resources when the service is destroyed:
   * 1. Stops connectivity monitoring
   * 2. Disconnects Deepgram WebSocket client
   * 3. Clears all references for garbage collection
   * Note: AudioCaptureService cleanup is handled automatically by its ngOnDestroy
   */
  ngOnDestroy() {
    this.stopConnectivityMonitoring();
    this.audioCapture = undefined;

    this.listenLive?.disconnect();
    this.listenLive = undefined;
  }
}
